// Pure helpers for Sales Gallery category management (admin ContentManagement).
// The persistent category list lives in content['sales_gallery_category_order'].content.order.

const ITEM_PREFIX = 'sales_gallery_item_';

const itemKeys = (content) =>
  Object.keys(content).filter((k) => k.startsWith(ITEM_PREFIX));

// Merged display list: saved order entries first (kept even when empty), then
// item categories that are not yet in the saved order (first appearance wins).
export function categoryList(savedOrder, content) {
  const extra = itemKeys(content)
    .map((k) => content[k]?.content?.category)
    .filter(Boolean)
    .filter((c, i, arr) => !savedOrder.includes(c) && arr.indexOf(c) === i);
  return [...savedOrder, ...extra];
}

// Keys of items in the given category, sorted by their order field.
export function itemKeysInCategory(content, category) {
  return itemKeys(content)
    .filter((k) => content[k]?.content?.category === category)
    .sort((a, b) => (content[a]?.content?.order || 0) - (content[b]?.content?.order || 0));
}

// Returns an error message (Indonesian) or null when the rename is valid.
// The trimmed name equals oldName -> null (valid no-op).
export function validateRename(list, oldName, rawNewName) {
  const trimmed = (rawNewName || '').trim();
  if (!trimmed) return 'Nama kategori tidak boleh kosong';
  if (trimmed !== oldName && list.includes(trimmed)) return `Kategori "${trimmed}" sudah ada`;
  return null;
}

// Returns { content, renamedKeys }.
// Rewrites category on all items of oldName and replaces the name inside the
// saved order array (in place). When oldName is absent from the order array the
// new name is appended; the config section is created if it does not exist yet.
// newName is assumed already validated; content is not mutated.
export function renameCategory(content, oldName, newName) {
  const next = { ...content };
  const renamedKeys = [];

  for (const key of itemKeys(content)) {
    if (content[key]?.content?.category === oldName) {
      next[key] = {
        ...content[key],
        content: { ...content[key].content, category: newName },
      };
      renamedKeys.push(key);
    }
  }

  const existing = next.sales_gallery_category_order;
  const order = existing?.content?.order ? [...existing.content.order] : null;

  if (order) {
    const idx = order.indexOf(oldName);
    if (idx >= 0) order[idx] = newName;
    else if (!order.includes(newName)) order.push(newName);
    next.sales_gallery_category_order = {
      ...existing,
      content: { ...existing.content, order },
    };
  } else {
    // Config missing: build it from current item categories with the rename applied.
    const base = categoryList([], next).map((c) => (c === oldName ? newName : c));
    if (!base.includes(newName)) base.push(newName);
    next.sales_gallery_category_order = {
      page: 'sales_gallery',
      section: 'category_order',
      content_type: 'config',
      content: { order: base },
    };
  }

  return { content: next, renamedKeys };
}

// Returns { content, error }. Appends a validated new category to the saved
// order (creating the config section when missing). content is not mutated.
export function addCategory(content, rawName) {
  const name = (rawName || '').trim();
  const list = categoryList(content.sales_gallery_category_order?.content?.order || [], content);
  const error = validateRename(list, '', name);
  if (error) return { content, error };

  const existing = content.sales_gallery_category_order;
  return {
    content: {
      ...content,
      sales_gallery_category_order: {
        ...(existing || {}),
        page: 'sales_gallery',
        section: 'category_order',
        content_type: 'config',
        content: { ...(existing?.content || {}), order: [...list, name] },
      },
    },
    error: null,
  };
}

// Returns { content, found }: removes category from the saved order array.
// Does not create the config section when it is missing (found = false).
export function removeCategoryFromOrder(content, category) {
  const existing = content.sales_gallery_category_order;
  const order = existing?.content?.order;
  if (!order || !order.includes(category)) return { content, found: false };
  return {
    content: {
      ...content,
      sales_gallery_category_order: {
        ...existing,
        content: { ...existing.content, order: order.filter((c) => c !== category) },
      },
    },
    found: true,
  };
}
